import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Sidebar } from '../../shared/sidebar/sidebar';
import { TournamentService } from '../../core/services/tournament.service';
import { Tournament, CreateTournamentRequest } from '../../core/models/tournament.model';
import { BranchService } from '../../core/services/branch.service';
import { AppConfigService } from '../../core/services/app-config.service';
import { Branch } from '../../core/models/branch.model';

@Component({
  selector: 'app-tournaments',
  imports: [CommonModule, FormsModule, Sidebar],
  templateUrl: './tournaments.html',
  styleUrl: './tournaments.scss',
})
export class Tournaments implements OnInit {
  searchQuery: string = '';
  currentPage: number = 1;
  itemsPerPage: number = 7;
  totalPages: number = 1;
  visiblePages: number[] = [];
  isLoading: boolean = false;

  tournaments: Tournament[] = [];
  filteredTournaments: Tournament[] = [];

  // Add tournament sidebar
  showAddSidebar: boolean = false;
  isCreating: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';

  // Dropdowns data
  sports: string[] = [];
  branches: Branch[] = [];

  // Form data
  tournamentForm: CreateTournamentRequest = {
    name: '',
    sport: '',
    description: '',
    startDate: '',
    endDate: '',
    branch: '',
    maxTeams: 0,
    maxIndividuals: 0,
    teamRegistrationFee: 0,
    teamRegistrationFeeAcademy: 0,
    individualRegistrationFee: 0,
    individualRegistrationFeeAcademy: 0,
    showOnMobileHome: false,
    isIndividualRegistrationEnabled: true,
    isTeamRegistrationEnabled: true
  };

  constructor(
    private tournamentService: TournamentService,
    private branchService: BranchService,
    private appConfigService: AppConfigService,
    private router: Router
  ) {}

  ngOnInit() {
    this.loadTournaments();
    this.loadSports();
    this.loadBranches();
  }

  loadSports() {
    this.appConfigService.getSports().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.sports = response.data;
        }
      },
      error: (error) => {
        console.error('Error loading sports:', error);
      }
    });
  }

  loadBranches() {
    this.branchService.getBranches().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.branches = response.data;
        }
      },
      error: (error) => {
        console.error('Error loading branches:', error);
      }
    });
  }

  loadTournaments() {
    this.isLoading = true;
    this.tournamentService.getTournaments().subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.success) {
          this.tournaments = response.data;
          this.filteredTournaments = this.tournaments;
          this.calculatePagination();
        }
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Error loading tournaments:', error);
      }
    });
  }

  calculatePagination() {
    this.totalPages = Math.ceil(this.filteredTournaments.length / this.itemsPerPage);
    this.visiblePages = Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  get paginatedTournaments() {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return this.filteredTournaments.slice(start, end);
  }

  onSearch() {
    if (!this.searchQuery.trim()) {
      this.filteredTournaments = this.tournaments;
    } else {
      this.filteredTournaments = this.tournaments.filter(tournament =>
        tournament.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        tournament.sport.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        tournament.branch.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    }
    this.currentPage = 1;
    this.calculatePagination();
  }

  goToPage(page: number) {
    this.currentPage = page;
  }

  getGradientColor(index: number): string {
    const gradients = [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
      'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)'
    ];
    return gradients[index % gradients.length];
  }

  getStatus(tournament: Tournament): string {
    const now = new Date();
    const start = new Date(tournament.startDate);
    const end = new Date(tournament.endDate);

    if (now < start) return 'Upcoming';
    if (now > end) return 'Completed';
    return 'Ongoing';
  }

  viewTournament(id: string) {
    this.router.navigate(['/tournaments', id]);
  }

  openAddSidebar() {
    this.showAddSidebar = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.resetForm();
  }

  closeAddSidebar() {
    this.showAddSidebar = false;
    this.resetForm();
    this.errorMessage = '';
    this.successMessage = '';
  }

  resetForm() {
    this.tournamentForm = {
      name: '',
      sport: '',
      description: '',
      startDate: '',
      endDate: '',
      branch: '',
      maxTeams: 0,
      maxIndividuals: 0,
      teamRegistrationFee: 0,
      teamRegistrationFeeAcademy: 0,
      individualRegistrationFee: 0,
      individualRegistrationFeeAcademy: 0,
      showOnMobileHome: false,
      isIndividualRegistrationEnabled: true,
      isTeamRegistrationEnabled: true
    };
  }

  createTournament() {
    this.errorMessage = '';
    this.successMessage = '';

    // Basic validation
    if (!this.tournamentForm.name || !this.tournamentForm.sport || !this.tournamentForm.branch) {
      this.errorMessage = 'Please fill in all required fields';
      return;
    }

    if (!this.tournamentForm.startDate || !this.tournamentForm.endDate) {
      this.errorMessage = 'Please select start and end dates';
      return;
    }

    if (new Date(this.tournamentForm.endDate) < new Date(this.tournamentForm.startDate)) {
      this.errorMessage = 'End date must be after start date';
      return;
    }

    this.isCreating = true;

    this.tournamentService.createTournament(this.tournamentForm).subscribe({
      next: (response) => {
        this.isCreating = false;
        if (response.success) {
          this.successMessage = 'Tournament created successfully';
          this.closeAddSidebar();
          this.loadTournaments();
          setTimeout(() => this.successMessage = '', 3000);
        }
      },
      error: (error) => {
        this.isCreating = false;
        this.errorMessage = error.error?.message || 'Failed to create tournament';
        console.error('Error creating tournament:', error);
      }
    });
  }
}
